import {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {useAuth} from './auth';
import {api} from './api';
import './Entitlements.css';

export function useEntitlements() {
  const {user} = useAuth();
  const identity = `${user?.user_id}:${user?.role}:${user?.exhibitor_id}`;
  const [state, setState] = useState(null);
  useEffect(() => {
    let alive = true, timer, controller;
    setState(null);
    if (!user?.exhibitor_id || !['exhibitor','admin'].includes(user.role)) return;
    const refresh = async () => {
      controller?.abort(); controller = new AbortController();
      try {
        const data = await api.communication('/me/entitlements', undefined, {signal:controller.signal});
        if (alive && typeof data?.products === 'number') setState({identity,data});
      } catch (error) {if (alive && error.name !== 'AbortError') setState(null);}
    };
    refresh(); timer = setInterval(refresh,15000);
    window.addEventListener('focus',refresh); window.addEventListener('entitlements-refresh',refresh);
    return () => {alive=false;clearInterval(timer);controller?.abort();window.removeEventListener('focus',refresh);window.removeEventListener('entitlements-refresh',refresh);};
  },[identity,user?.exhibitor_id,user?.role]);
  return state?.identity === identity ? state.data : null;
}

export function UpgradePlan({children='Upgrade Plan'}) {
  return <Link className="entitlement-upgrade" to="/pricing?upgrade=1">{children}</Link>;
}

const labels = {booth_views:'Booth views',product_views:'Product views',live_viewers:'Recorded unique LIVE viewers',inquiry_count:'Buyer inquiries',meeting_requests:'Meeting requests',shortlist_count:'Booth shortlists',top_performing_products:'Top-performing products',buyer_engagement_trends:'Buyer engagement trends',country_market_breakdown:'Country / market breakdown',post_show_summary:'Post-show engagement summary',meeting_conversion:'Meeting-conversion overview'};
labels.shortlist_count='Booth and product shortlists';
labels.top_performing_products='Top products by recorded inquiries + shortlists';
labels.buyer_engagement_trends='Recorded inquiry trend (last 30 recorded dates)';
function MetricValue({value}) {
  if(Array.isArray(value))return value.length?<ul>{value.map((row,i)=><li key={row.product_id||row.date||i}>{row.name||row.date}: {row.inquiries} inquiries{row.shortlists!==undefined?` · ${row.shortlists} shortlists`:''}</li>)}</ul>:'No recorded engagement';
  if(value&&typeof value==='object')return <>{value.confirmed_or_completed} confirmed or completed / {value.requested_total} requests</>;
  return <>{value}</>;
}

export function EntitlementPanel() {
  const policy = useEntitlements();
  const [data,setData] = useState(null),[error,setError] = useState('');
  const version=policy?.version, tier=policy?.analytics, booth=policy?.exhibitor_id;
  useEffect(()=>{
    setData(null);setError('');
    if(!tier||tier==='none')return;
    const controller=new AbortController();
    api.communication(`/me/analytics?advanced=${tier==='advanced'}`,undefined,{signal:controller.signal})
      .then(values=>{if(!controller.signal.aborted)setData({version,booth,values});}).catch(e=>{if(e.name!=='AbortError')setError('Analytics are temporarily unavailable.');});
    return()=>controller.abort();
  },[version,tier,booth]);
  if(!policy)return <div className="entitlement-panel">Package details are loading or unavailable.</div>;
  return <section className="entitlement-panel" data-testid="entitlement-panel">
    <div className="entitlement-heading"><strong>{policy.package_id} package</strong><UpgradePlan/></div>
    <p>{policy.public_cameras} LIVE cameras · {policy.private_rooms} private rooms · {policy.product_count}/{policy.products} products</p>
    {!!policy.reserved_product_slots&&<p role="status">{policy.reserved_product_slots} product slot(s) are reserved by interrupted uploads. An administrator must review them before capacity is released.</p>}
    {policy.product_count>=policy.products&&<p role="status">Product limit reached: {policy.product_count}/{policy.products}. Existing products are retained. {policy.upgrade&&<>Upgrade to {policy.upgrade.package_id} for up to {policy.upgrade.products} products. </>}<UpgradePlan/></p>}
    <small>{policy.visibility} listing priority · {policy.support} support tier</small>
    <p><strong>{tier==='none'?'Business Analytics 🔒':tier==='advanced'?'Advanced Analytics':'Business Analytics'}</strong></p>
    {tier==='none'&&<p>Available on Professional. <UpgradePlan/></p>}
    {tier==='business'&&<p>Advanced Analytics 🔒 Available on Premium. <UpgradePlan/></p>}
    {error&&<p role="status">{error}</p>}
    {tier!=='none'&&data?.version===version&&data?.booth===booth&&<dl className="entitlement-metrics">{Object.entries(data.values).filter(([key])=>key!=='tier').map(([key,value])=><div key={key}><dt>{labels[key]||key}</dt><dd><MetricValue value={value}/></dd></div>)}</dl>}
    <small>Uncaptured metrics are labelled Not implemented. Support is a service commitment; it does not imply an automated SLA.</small>
  </section>;
}
